/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * UpdateApplicationModal
 * -----------------------
 * Drives the whole pack-update flow for one application as internal step
 * state: fetch the update plan, show a summary, resolve any conflicts,
 * apply, show the result (including any skipped rows).
 *
 * Props:
 * - theme: Theme object (passed in, not read via useTheme — matches
 *   TableDetailsModal's convention).
 * - applicationId: The application's id (string or number).
 * - onClose: Called when the modal should be dismissed.
 * - onApplied: Called once with the apply-update response JSON, right
 *   after a successful apply.
 */
import React, { useEffect, useState } from 'react';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

function formatDiffValue(value) {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function ConflictRow({ kind, row, resolution, onResolve }) {
    const attrs = Object.entries(row.diff || {});
    return (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem', marginBottom: '0.75rem' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <span style={{ fontWeight: 700 }}>{row.name}</span>
                <span className="badge bg-light text-dark border">{kind}</span>
            </div>

            {row.remote_deleted && (
                <div className="small text-danger mb-2">The pack has deleted this {kind}. Choosing "Use Remote" will delete it here too.</div>
            )}

            {attrs.length > 0 && (
                <table style={{ width: '100%', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left', padding: '0.25rem' }}>Attribute</th>
                            <th style={{ textAlign: 'left', padding: '0.25rem' }}>Local</th>
                            <th style={{ textAlign: 'left', padding: '0.25rem' }}>Remote</th>
                        </tr>
                    </thead>
                    <tbody>
                        {attrs.map(([attr, { local, remote }]) => (
                            <tr key={attr}>
                                <td style={{ padding: '0.25rem', fontWeight: 600 }}>{attr}</td>
                                <td style={{ padding: '0.25rem' }}>{formatDiffValue(local)}</td>
                                <td style={{ padding: '0.25rem' }}>{formatDiffValue(remote)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <div className="d-flex gap-3">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                        type="radio"
                        name={`${kind}-${row.pack_id}`}
                        aria-label={`Keep Local for ${row.name}`}
                        checked={resolution === 'local'}
                        onChange={() => onResolve('local')}
                    />
                    Keep Local
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                        type="radio"
                        name={`${kind}-${row.pack_id}`}
                        aria-label={`Use Remote for ${row.name}`}
                        checked={resolution === 'remote'}
                        onChange={() => onResolve('remote')}
                    />
                    Use Remote
                </label>
            </div>
        </div>
    );
}

export default function UpdateApplicationModal({ theme, applicationId, onClose, onApplied }) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
    const [step, setStep] = useState('loading'); // loading | up_to_date | summary | resolving | applying | result | error
    const [plan, setPlan] = useState(null);
    const [tableResolutions, setTableResolutions] = useState({});
    const [fieldResolutions, setFieldResolutions] = useState({});
    const [applyResult, setApplyResult] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 991);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await apiHelper.apiCall(`/arcee/application/${applicationId}/check-update/`, { method: 'GET' });
                const json = await resp.json();
                if (cancelled) return;
                if (!resp.ok) {
                    setErrorMessage(json.error || json.message || 'Failed to check for updates.');
                    setStep('error');
                    return;
                }
                setPlan(json);
                setStep(json.up_to_date ? 'up_to_date' : 'summary');
            } catch (e) {
                if (cancelled) return;
                setErrorMessage(e.message || 'Failed to check for updates.');
                setStep('error');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId]);

    if (step === 'loading') return null;

    const conflictTables = (plan?.tables || []).filter(t => t.action === 'conflict');
    const conflictFields = (plan?.fields || []).filter(f => f.action === 'conflict');
    const autoApplyCount = (plan?.tables || []).filter(t => t.action === 'auto_apply').length
        + (plan?.fields || []).filter(f => f.action === 'auto_apply').length;
    const keepLocalCount = (plan?.tables || []).filter(t => t.action === 'keep_local').length
        + (plan?.fields || []).filter(f => f.action === 'keep_local').length;
    const hasConflicts = conflictTables.length + conflictFields.length > 0;
    const allConflictsResolved = conflictTables.every(t => tableResolutions[t.pack_id])
        && conflictFields.every(f => fieldResolutions[f.pack_id]);

    const handleApply = async () => {
        setStep('applying');
        try {
            const resp = await apiHelper.apiCall(`/arcee/application/${applicationId}/apply-update/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ table_resolutions: tableResolutions, field_resolutions: fieldResolutions }),
            });
            const json = await resp.json();
            if (!resp.ok) {
                setErrorMessage(json.error || json.message || 'Failed to apply update.');
                setStep('error');
                return;
            }
            setApplyResult(json);
            setStep('result');
            onApplied && onApplied(json);
        } catch (e) {
            setErrorMessage(e.message || 'Failed to apply update.');
            setStep('error');
        }
    };

    return (
        <div
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 3000,
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                padding: isMobile ? '0.5rem' : '1rem', overflow: 'auto',
            }}
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="update-application-title"
                onClick={e => e.stopPropagation()}
                style={{
                    background: theme.footer, borderRadius: theme.cardRadius,
                    width: isMobile ? '98%' : '90%', maxWidth: '700px', maxHeight: '90vh',
                    overflowY: 'auto', overflowX: 'hidden',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    padding: isMobile ? '1rem' : '1.5rem', color: theme.text,
                    fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2 id="update-application-title" style={{ margin: 0, fontWeight: 700, fontSize: isMobile ? '1.25rem' : '1.5rem', color: theme.primary }}>
                        Application Update
                    </h2>
                    <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: '1.7rem', fontWeight: 'bold', color: theme.secondary, cursor: 'pointer' }}>
                        &times;
                    </button>
                </div>

                {step === 'up_to_date' && (
                    <div>
                        <p>Your application is already up to date (version {plan.to_version}).</p>
                        <div className="d-flex justify-content-end">
                            <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        </div>
                    </div>
                )}

                {step === 'summary' && (
                    <div>
                        <p>An update is available: version {plan.from_version} → {plan.to_version}.</p>
                        <ul>
                            {autoApplyCount > 0 && <li>{autoApplyCount} change(s) will be applied automatically.</li>}
                            {keepLocalCount > 0 && <li>{keepLocalCount} of your local change(s) will be kept as-is.</li>}
                            {hasConflicts && <li>{conflictTables.length + conflictFields.length} conflict(s) need your decision.</li>}
                        </ul>
                        <div className="d-flex justify-content-end gap-2">
                            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            {hasConflicts ? (
                                <button className="btn btn-primary" onClick={() => setStep('resolving')}>
                                    Resolve Conflicts
                                </button>
                            ) : (
                                <button className="btn btn-primary" onClick={handleApply}>Apply Update</button>
                            )}
                        </div>
                    </div>
                )}

                {step === 'resolving' && (
                    <div>
                        {conflictTables.map(t => (
                            <ConflictRow
                                key={`table-${t.pack_id}`}
                                kind="table"
                                row={t}
                                resolution={tableResolutions[t.pack_id]}
                                onResolve={(value) => setTableResolutions(prev => ({ ...prev, [t.pack_id]: value }))}
                            />
                        ))}
                        {conflictFields.map(f => (
                            <ConflictRow
                                key={`field-${f.pack_id}`}
                                kind="field"
                                row={f}
                                resolution={fieldResolutions[f.pack_id]}
                                onResolve={(value) => setFieldResolutions(prev => ({ ...prev, [f.pack_id]: value }))}
                            />
                        ))}
                        <div className="d-flex justify-content-end gap-2 mt-2">
                            <button className="btn btn-secondary" onClick={() => setStep('summary')}>Back</button>
                            <button className="btn btn-primary" disabled={!allConflictsResolved} onClick={handleApply}>
                                Apply Update
                            </button>
                        </div>
                    </div>
                )}

                {step === 'applying' && <p>Applying update…</p>}

                {step === 'result' && applyResult && (
                    <div>
                        <p>Update applied. Application is now at version {applyResult.new_version}.</p>
                        {applyResult.skipped.length > 0 && (
                            <div>
                                <p className="fw-bold mb-2">The following couldn't be applied automatically:</p>
                                {applyResult.skipped.map((s, i) => (
                                    <div key={i} style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 8, padding: '0.5rem 0.75rem', marginBottom: '0.5rem' }}>
                                        <div style={{ fontWeight: 700 }}>{s.name} <span className="badge bg-light text-dark border ms-2">{s.kind}</span></div>
                                        <div className="small">{s.reason}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="d-flex justify-content-end">
                            <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        </div>
                    </div>
                )}

                {step === 'error' && (
                    <div>
                        <p className="text-danger">{errorMessage}</p>
                        <div className="d-flex justify-content-end">
                            <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
